import {
  COLOR_UNLOCK_DAYS,
  CONSUMPTION_TIERS,
  SPAWN_INTERVAL_RANGES,
} from '../config/balance.js';
import { COLOR_IDS, type ColorId } from '../config/colors.js';
import {
  canPlaceBuilding,
  cellKey,
  defaultPortOffset,
  findFreeAnchorsForKind,
  type OccupancyContext,
} from './grid.js';
import {
  canSpawnBulbForColor,
  canSpawnGeneratorForColor,
  colorNeedsGenerator,
  isProductionAtCap,
  maxProductionPerSecond,
  meetsProductionSurplus,
  productionSurplusRatio,
  totalConsumptionPerSecond,
} from './energy.js';
import { restoreRng } from './rng.js';
import type { GameNode, GameState, PendingSpawnEvent, SimulationEvent } from './types.js';

function tierRateForDay(day: number): number {
  const tier = pickConsumptionTierForDay(day);
  return CONSUMPTION_TIERS.find((t) => t.id === tier)?.rate ?? 0.75;
}

function prodM(state: GameState): number {
  return Math.max(0, state.runModifiers?.generatorProductionMult ?? 0);
}

export function nextSpawnDelay(state: GameState): number {
  // Always pick a band — late game falls into the last (endless) range.
  const range =
    SPAWN_INTERVAL_RANGES.find(
      (r) => state.day >= r.minDay && state.day <= r.maxDay,
    ) ?? SPAWN_INTERVAL_RANGES[SPAWN_INTERVAL_RANGES.length - 1]!;
  const r = state.rng;
  const seedMix = (r.seed ^ Math.floor(state.simTime * 100)) >>> 0;
  const frac = (seedMix % 997) / 997;
  const delay = range.minSec + frac * (range.maxSec - range.minSec);
  // Guard against NaN/0 so the director never stalls forever.
  return Number.isFinite(delay) && delay > 0 ? delay : range.minSec;
}

export function unlockedColors(day: number): ColorId[] {
  return COLOR_IDS.filter((c) => day >= COLOR_UNLOCK_DAYS[c]);
}

export function pickSpawnColor(state: GameState): ColorId {
  const available = unlockedColors(state.day);
  const idx = state.rng.seed % available.length;
  return available[idx]!;
}

function pickFrom(state: GameState, colors: ColorId[]): ColorId {
  return colors[state.rng.seed % colors.length]!;
}

export function pickConsumptionTierForDay(day: number): GameNode['consumptionTier'] {
  let tier: GameNode['consumptionTier'] = CONSUMPTION_TIERS[0]!.id;
  for (const t of CONSUMPTION_TIERS) {
    if (day >= t.unlockDay) tier = t.id;
  }
  return tier;
}

function cableCellSet(state: GameState): Set<string> {
  const s = new Set<string>();
  for (const c of state.cables) {
    for (const cell of c.cells) s.add(cellKey(cell));
  }
  return s;
}

function occupancy(state: GameState): OccupancyContext {
  return {
    gridWidth: state.gridWidth,
    gridHeight: state.gridHeight,
    terrain: state.terrain,
    nodes: state.nodes,
    cableCells: cableCellSet(state),
  };
}

export function createGeneratorNode(
  state: GameState,
  color: ColorId,
  anchor: { x: number; y: number },
): GameNode {
  const id = `n${state.nextNodeId++}`;
  return {
    id,
    kind: 'generator',
    color,
    anchor,
    generatorSize: 'small',
    productionTimer: 0,
  };
}

export function createBulbNode(
  state: GameState,
  color: ColorId,
  anchor: { x: number; y: number },
  day: number,
): GameNode {
  const id = `n${state.nextNodeId++}`;
  return {
    id,
    kind: 'bulb',
    color,
    anchor,
    portOffset: defaultPortOffset('bulb'),
    consumptionTier: pickConsumptionTierForDay(day),
    energy: 100 * state.runModifiers.bulbCapacityMult,
  };
}

function placeNode(state: GameState, node: GameNode): boolean {
  const ctx = occupancy(state);
  if (!canPlaceBuilding(ctx, node.anchor, node.kind)) return false;
  state.nodes.push(node);
  return true;
}

function pickAnchor(
  state: GameState,
  kind: 'generator' | 'bulb',
): { x: number; y: number } | null {
  const ctx = occupancy(state);
  const anchors = findFreeAnchorsForKind(ctx, kind);
  if (anchors.length === 0) return null;
  const rng = restoreRng(state.rng);
  const pick = rng.pick(anchors);
  state.rng.seed = rng.state.seed;
  return pick;
}

function chebyshev(
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

function minDistTo(
  anchor: { x: number; y: number },
  gens: readonly GameNode[],
): number {
  let best = Infinity;
  for (const g of gens) {
    const d = chebyshev(anchor, g.anchor);
    if (d < best) best = d;
  }
  return best;
}

/**
 * Soft neighborhood score: prefer cells near same-color generators
 * (quartiers), with a floor so new districts remain possible.
 * Slightly avoid hugging foreign-color gens.
 */
export function generatorNeighborhoodScore(
  anchor: { x: number; y: number },
  sameColorGens: readonly GameNode[],
  foreignGens: readonly GameNode[],
): number {
  if (sameColorGens.length === 0) return 1;
  const dSame = minDistTo(anchor, sameColorGens);
  let score: number;
  if (dSame <= 1) score = 3.2;
  else if (dSame <= 3) score = 4.5; // sweet spot — fills a quartier
  else if (dSame <= 5) score = 2.2;
  else if (dSame <= 8) score = 0.55;
  else score = 0.18; // far = potential new district

  if (foreignGens.length > 0) {
    const dForeign = minDistTo(anchor, foreignGens);
    if (dForeign <= 1) score *= 0.35;
    else if (dForeign <= 2) score *= 0.65;
  }
  return score;
}

/**
 * Pick a free generator cell biased toward same-color neighbors.
 * ~12% of the time (when same-color gens exist), prefer a far cell to
 * seed another quartier of that color.
 */
export function pickGeneratorAnchor(
  state: GameState,
  color: ColorId,
): { x: number; y: number } | null {
  const ctx = occupancy(state);
  const free = findFreeAnchorsForKind(ctx, 'generator');
  if (free.length === 0) return null;

  const same: GameNode[] = [];
  const foreign: GameNode[] = [];
  for (const n of state.nodes) {
    if (n.kind !== 'generator' || !n.color) continue;
    if (n.color === color) same.push(n);
    else foreign.push(n);
  }

  const rng = restoreRng(state.rng);

  if (same.length === 0) {
    // First of this color — random free cell (new quartier seed).
    const pick = rng.pick(free);
    state.rng.seed = rng.state.seed;
    return pick;
  }

  // Occasionally open a second/third district of the same color.
  if (rng.next() < 0.12) {
    const far = free.filter((a) => minDistTo(a, same) >= 6);
    if (far.length > 0) {
      const pick = rng.pick(far);
      state.rng.seed = rng.state.seed;
      return pick;
    }
  }

  const weights = free.map((a) =>
    generatorNeighborhoodScore(a, same, foreign),
  );
  const total = weights.reduce((s, w) => s + w, 0);
  if (total <= 0) {
    const pick = rng.pick(free);
    state.rng.seed = rng.state.seed;
    return pick;
  }

  let slot = rng.next() * total;
  state.rng.seed = rng.state.seed;
  for (let i = 0; i < free.length; i++) {
    slot -= weights[i]!;
    if (slot <= 0) return free[i]!;
  }
  return free[free.length - 1]!;
}

export function trySpawnGenerator(state: GameState, color: ColorId): GameNode | null {
  const rate = tierRateForDay(state.day);
  if (!canSpawnGeneratorForColor(color, state.nodes, rate, prodM(state))) return null;
  const anchor = pickGeneratorAnchor(state, color);
  if (!anchor) return null;
  const node = createGeneratorNode(state, color, anchor);
  if (!placeNode(state, node)) return null;
  return node;
}

export function trySpawnBulb(state: GameState, color: ColorId): GameNode | null {
  const tier = pickConsumptionTierForDay(state.day);
  const rate = CONSUMPTION_TIERS.find((t) => t.id === tier)?.rate ?? 0.75;
  if (!canSpawnBulbForColor(color, state.nodes, rate, prodM(state))) {
    return null;
  }
  const anchor = pickAnchor(state, 'bulb');
  if (!anchor) return null;
  const node = createBulbNode(state, color, anchor, state.day);
  if (!placeNode(state, node)) return null;
  return node;
}

export function processPendingSpawns(state: GameState): SimulationEvent[] {
  const events: SimulationEvent[] = [];
  const remaining: PendingSpawnEvent[] = [];

  const rate = tierRateForDay(state.day);
  for (const ev of state.pendingSpawns) {
    if (ev.kind === 'generator') {
      if (colorNeedsIntroduction(state, ev.color)) {
        // Never let a new color arrive as a lone generator.
        remaining.push({ kind: 'pair', color: ev.color, attempts: ev.attempts });
        continue;
      }
      if (!canSpawnGeneratorForColor(ev.color, state.nodes, rate, prodM(state))) {
        // Soft-cap temporarily blocks gens — keep trying a few ticks.
        if (ev.attempts < 8) remaining.push({ ...ev, attempts: ev.attempts + 1 });
        continue;
      }
      const g = trySpawnGenerator(state, ev.color);
      if (g) events.push({ type: 'nodeSpawned', node: g });
      else if (ev.attempts < 8) remaining.push({ ...ev, attempts: ev.attempts + 1 });
    } else if (ev.kind === 'bulb') {
      if (colorNeedsIntroduction(state, ev.color)) {
        events.push(...introduceColorPackage(state, ev.color));
        if (colorNeedsIntroduction(state, ev.color) && ev.attempts < 8) {
          remaining.push({ kind: 'pair', color: ev.color, attempts: ev.attempts + 1 });
        }
        continue;
      }
      for (const g of beforeSpawnBulbEnsureSurplus(state, ev.color, rate)) {
        events.push({ type: 'nodeSpawned', node: g });
      }
      const b = trySpawnBulb(state, ev.color);
      if (b) events.push({ type: 'nodeSpawned', node: b });
      else if (ev.attempts < 8) remaining.push({ ...ev, attempts: ev.attempts + 1 });
    } else if (ev.kind === 'pair') {
      events.push(...introduceColorPackage(state, ev.color));
      if (colorNeedsIntroduction(state, ev.color) && ev.attempts < 8) {
        remaining.push({ kind: 'pair', color: ev.color, attempts: ev.attempts + 1 });
      }
    }
  }

  state.pendingSpawns = remaining;
  return events;
}

export function tickSpawnDirector(state: GameState): SimulationEvent[] {
  const events: SimulationEvent[] = [];
  events.push(...processPendingSpawns(state));

  if (state.gameOver || state.waitingForReward) return events;

  state.spawnTimer -= 0; // updated in simulation with dt

  return events;
}

/** True when this color has no generator and no bulb on the map yet. */
export function isNewColorOnMap(state: GameState, color: ColorId): boolean {
  return !state.nodes.some(
    (n) => n.color === color && (n.kind === 'generator' || n.kind === 'bulb'),
  );
}

/**
 * Unlocked color that still has no bulb — introduction incomplete
 * (even if a stray generator already landed).
 */
export function colorNeedsIntroduction(state: GameState, color: ColorId): boolean {
  if (state.day < COLOR_UNLOCK_DAYS[color]) return false;
  return !state.nodes.some((n) => n.kind === 'bulb' && n.color === color);
}

/** @deprecated Prefer colorNeedsIntroduction — same meaning for pairing. */
export function mustPairGeneratorWithBulb(state: GameState, color: ColorId): boolean {
  return colorNeedsIntroduction(state, color);
}

/**
 * First appearance of a color: ≥1 bulb + enough gens for the surplus floor.
 * Retries via pendingSpawns if space is tight.
 */
export function introduceColorPackage(state: GameState, color: ColorId): SimulationEvent[] {
  const events: SimulationEvent[] = [];
  const rate = tierRateForDay(state.day);

  for (const g of beforeSpawnBulbEnsureSurplus(state, color, rate)) {
    events.push({ type: 'nodeSpawned', node: g });
  }
  if (!state.nodes.some((n) => n.kind === 'generator' && n.color === color)) {
    const g = trySpawnGenerator(state, color);
    if (g) events.push({ type: 'nodeSpawned', node: g });
  }

  const b = trySpawnBulb(state, color);
  if (b) events.push({ type: 'nodeSpawned', node: b });

  for (const g of topUpGeneratorsForColor(state, color)) {
    events.push({ type: 'nodeSpawned', node: g });
  }

  // Still under floor after bulb (discrete steps) → keep topping gens.
  let guard = 0;
  while (
    state.nodes.some((n) => n.kind === 'bulb' && n.color === color) &&
    !meetsProductionSurplus(color, state.nodes, prodM(state)) &&
    guard++ < 8
  ) {
    const rate = tierRateForDay(state.day);
    if (!canSpawnGeneratorForColor(color, state.nodes, rate, prodM(state))) break;
    const g = trySpawnGenerator(state, color);
    if (!g) break;
    events.push({ type: 'nodeSpawned', node: g });
  }

  return events;
}

/**
 * Bias spawns toward the +30%…+50% band:
 * under floor → generator; at/over cap → bulb only; else mixed.
 * Colors without a first bulb always introduce as a full package.
 */
export function rollSpawnEvent(state: GameState): PendingSpawnEvent {
  const available = unlockedColors(state.day);
  const rate = tierRateForDay(state.day);

  // Absolute priority: any unlocked color still missing its first bulb.
  const needIntro = available.filter((c) => colorNeedsIntroduction(state, c));
  if (needIntro.length > 0) {
    return { kind: 'pair', color: pickFrom(state, needIntro), attempts: 0 };
  }

  const underMin = available.filter((c) => colorNeedsGenerator(c, state.nodes, prodM(state)));
  const atCap = available.filter((c) => isProductionAtCap(c, state.nodes, prodM(state)));
  const inBand = available.filter(
    (c) => !colorNeedsGenerator(c, state.nodes, prodM(state)) && !isProductionAtCap(c, state.nodes, prodM(state)),
  );

  if (underMin.length > 0) {
    const color = pickFrom(state, underMin);
    const roll = state.rng.seed % 100;
    return {
      kind: roll < 70 ? 'generator' : 'pair',
      color,
      attempts: 0,
    };
  }

  if (atCap.length > 0 && inBand.length === 0) {
    // Prefer a color that can actually place something at today's bulb rate.
    const canBulb = atCap.filter((c) => canSpawnBulbForColor(c, state.nodes, rate, prodM(state)));
    if (canBulb.length > 0) {
      return { kind: 'bulb', color: pickFrom(state, canBulb), attempts: 0 };
    }
    const canGen = atCap.filter((c) => canSpawnGeneratorForColor(c, state.nodes, rate));
    if (canGen.length > 0) {
      return { kind: 'generator', color: pickFrom(state, canGen), attempts: 0 };
    }
    // Should be rare after the rate-aware soft-cap fix; still pick a gen attempt.
    return { kind: 'generator', color: pickFrom(state, atCap), attempts: 0 };
  }

  const pool = inBand.length > 0 ? inBand : available;
  const color = pickFrom(state, pool);
  if (isProductionAtCap(color, state.nodes, prodM(state))) {
    if (canSpawnBulbForColor(color, state.nodes, rate, prodM(state))) {
      return { kind: 'bulb', color, attempts: 0 };
    }
    return { kind: 'generator', color, attempts: 0 };
  }
  const roll = state.rng.seed % 100;
  if (roll < 55) return { kind: 'bulb', color, attempts: 0 };
  if (roll < 85) return { kind: 'generator', color, attempts: 0 };
  return { kind: 'pair', color, attempts: 0 };
}

/**
 * Top up generators until a bulb of `rate` can spawn (or cap / space blocks us).
 * With gens at 0.5/s and bulbs ≥0.75/s, one bulb often needs several gens.
 */
export function beforeSpawnBulbEnsureSurplus(
  state: GameState,
  color: ColorId,
  rate: number,
): GameNode[] {
  const out: GameNode[] = [];
  let guard = 0;
  while (!canSpawnBulbForColor(color, state.nodes, rate, prodM(state)) && guard++ < 8) {
    if (!canSpawnGeneratorForColor(color, state.nodes, rate, prodM(state))) break;
    const g = trySpawnGenerator(state, color);
    if (!g) {
      state.pendingSpawns.unshift({ kind: 'generator', color, attempts: 0 });
      break;
    }
    out.push(g);
  }
  return out;
}

/** After a new-color pair, add gens until the color is no longer under the floor. */
export function topUpGeneratorsForColor(state: GameState, color: ColorId): GameNode[] {
  const out: GameNode[] = [];
  let guard = 0;
  const rate = tierRateForDay(state.day);
  while (colorNeedsGenerator(color, state.nodes, prodM(state)) && guard++ < 8) {
    if (!canSpawnGeneratorForColor(color, state.nodes, rate, prodM(state))) break;
    const g = trySpawnGenerator(state, color);
    if (!g) break;
    out.push(g);
  }
  return out;
}

export function runSpawnTick(state: GameState, dt: number): SimulationEvent[] {
  const events = processPendingSpawns(state);
  if (state.gameOver || state.waitingForReward) return events;

  state.spawnTimer -= dt;
  while (state.spawnTimer <= 0) {
    state.spawnTimer += nextSpawnDelay(state);
    const ev = rollSpawnEvent(state);
    const rate = tierRateForDay(state.day);

    if (ev.kind === 'pair' || colorNeedsIntroduction(state, ev.color)) {
      // First appearance of a color: bulb + enough matching generators.
      events.push(...introduceColorPackage(state, ev.color));
      if (colorNeedsIntroduction(state, ev.color)) {
        state.pendingSpawns.push({ kind: 'pair', color: ev.color, attempts: 0 });
      }
    } else if (ev.kind === 'bulb') {
      for (const g of beforeSpawnBulbEnsureSurplus(state, ev.color, rate)) {
        events.push({ type: 'nodeSpawned', node: g });
      }
      const b = trySpawnBulb(state, ev.color);
      if (b) events.push({ type: 'nodeSpawned', node: b });
      else state.pendingSpawns.push({ ...ev, attempts: 0 });
    } else {
      const g = trySpawnGenerator(state, ev.color);
      if (g) events.push({ type: 'nodeSpawned', node: g });
      else if (canSpawnGeneratorForColor(ev.color, state.nodes, rate, prodM(state))) {
        state.pendingSpawns.push({ ...ev, attempts: 0 });
      } else {
        // Soft-cap / space: queue a bulb attempt or gens for another hungry color.
        const alt = unlockedColors(state.day).find(
          (c) =>
            c !== ev.color &&
            (canSpawnBulbForColor(c, state.nodes, rate, prodM(state)) ||
              canSpawnGeneratorForColor(c, state.nodes, rate)),
        );
        if (alt) {
          state.pendingSpawns.push({
            kind: canSpawnBulbForColor(alt, state.nodes, rate, prodM(state)) ? 'bulb' : 'generator',
            color: alt,
            attempts: 0,
          });
        }
      }
    }

    state.rng.seed = (state.rng.seed + 0x9e3779b9) >>> 0;
  }

  return events;
}

export function getProductionSurplusRatio(color: ColorId, nodes: GameNode[]): number {
  return productionSurplusRatio(color, nodes);
}

// Re-export for callers that imported from spawn historically.
export { maxProductionPerSecond, totalConsumptionPerSecond };
